import type { JSX, MemoExoticComponent } from 'react';
import { useCallback, useMemo, useRef } from 'react';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import type { AnnotationTypes, AnnotationMarkStates } from '@atlaskit/adf-schema/annotation';
import type { AnnotationManager } from '@atlaskit/editor-common/annotation';
import type {
	InlineCommentSelectionComponentProps,
	InlineCommentViewComponentProps,
	InlineCommentHoverComponentProps,
} from '@atlaskit/editor-common/types/annotation';
import type { AnnotationState } from '@atlaskit/editor-plugin-annotation/types';

import { getRendererInlineCommentOptions } from '@confluence/annotation-provider-inline-comments/entry-points/getRendererInlineCommentOptions.ts';
import {
	useAnnotationsDispatch,
	useAnnotationManager,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { useIsHighlightOptionPanelEnabled } from '@confluence/comments-util/entry-points/useIsHighlightOptionPanelEnabled.ts';
import { useActiveInlineData } from '@confluence/experiment-inline-reactions/entry-points/useActiveInlineData.ts';
import { useInlineReactionMarkers } from '@confluence/experiment-inline-reactions/entry-points/useInlineReactionMarkers.ts';

export type RendererAnnotationProviderOptions = {
	pageId: string;
	validAnnotationsCallback: (validAnnotations: string[]) => void;
	triggerAnalyticsEvent: any;
	updateDocument: (adfDoc: JSONDocNode) => void;
	isArchived?: boolean;
};

export const useRendererAnnotationProviderOptions = ({
	pageId,
	validAnnotationsCallback,
	triggerAnalyticsEvent,
	updateDocument,
	isArchived,
}: RendererAnnotationProviderOptions): {
	inlineComment: {
		selectionComponent:
			| MemoExoticComponent<(props: InlineCommentSelectionComponentProps) => null>
			| undefined;
		viewComponent: MemoExoticComponent<
			(viewComponentProps: InlineCommentViewComponentProps) => JSX.Element
		>;
		hoverComponent: MemoExoticComponent<(props: InlineCommentHoverComponentProps) => null>;
		getState: (
			annotations: string[],
			isNestedRender: boolean,
		) => Promise<AnnotationState<AnnotationTypes, AnnotationMarkStates>[]>;
		updateSubscriber: any;
		disallowOnWhitespace: boolean;
		isToolbarAbove: boolean;
		allowDraftMode: boolean;
		allowCommentsOnMedia: boolean;
	};
	annotationManager: AnnotationManager | undefined;
} => {
	const { setSelectionProps, setViewProps, setHoverProps } = useAnnotationsDispatch();
	const { isHighlightOptionPanelEnabled } = useIsHighlightOptionPanelEnabled();
	const annotationManager = useAnnotationManager();
	const { reactionMarkerRefs, reactionMarkerMap } = useActiveInlineData(pageId);
	const { mergedReactionMarkerRefs, mergedReactionMarkerMap, optimisticResolvedMarkerRefs } =
		useInlineReactionMarkers({
			reactionMarkerRefs,
			reactionMarkerMap,
		});

	// Keep refs so the getters always return the latest merged values,
	// even if the renderer calls an old getState/viewComponent closure before
	// it receives the new annotationProvider prop. Crucially, these stable
	// getters are NOT in the useMemo deps — so a change to mergedReactionMarkerRefs
	// or mergedReactionMarkerMap alone does NOT create a new annotationProvider
	// options object. Without this, any optimistic reaction add/remove would
	// trigger a new options object → renderer re-initialises → async getState gap
	// → resolved highlight briefly snaps back to ACTIVE (CCCEMAU-2679).
	const reactionMarkerRefsRef = useRef(mergedReactionMarkerRefs);
	reactionMarkerRefsRef.current = mergedReactionMarkerRefs;
	const getReactionMarkerRefs = useCallback(() => reactionMarkerRefsRef.current, []);

	const reactionMarkerMapRef = useRef(mergedReactionMarkerMap);
	reactionMarkerMapRef.current = mergedReactionMarkerMap;
	const getReactionMarkerMap = useCallback(() => reactionMarkerMapRef.current, []);

	const optimisticResolvedMarkerRefsRef = useRef(optimisticResolvedMarkerRefs);
	optimisticResolvedMarkerRefsRef.current = optimisticResolvedMarkerRefs;
	const getOptimisticResolvedMarkerRefs = useCallback(
		() => optimisticResolvedMarkerRefsRef.current,
		[],
	);

	return useMemo(
		() => ({
			inlineComment: getRendererInlineCommentOptions({
				pageId,
				validAnnotationsCallback,
				triggerAnalyticsEvent,
				setSelectionProps,
				setHoverProps,
				setViewProps,
				updateDocument,
				isArchived,
				isHighlightOptionPanelEnabled,
				getReactionMarkerRefs,
				getReactionMarkerMap,
				getOptimisticResolvedMarkerRefs,
			}),
			annotationManager,
		}),
		[
			pageId,
			validAnnotationsCallback,
			triggerAnalyticsEvent,
			setSelectionProps,
			setHoverProps,
			setViewProps,
			updateDocument,
			isArchived,
			isHighlightOptionPanelEnabled,
			annotationManager,
			getReactionMarkerRefs,
			getReactionMarkerMap,
			getOptimisticResolvedMarkerRefs,
		],
	);
};
